/**
 * Strings, in two languages, typed.
 *
 * `docs/pwa.md` says Lingui, and this is not it. Lingui's ergonomics come from
 * a Babel macro, and the macro is a build-time dependency in every package that
 * wants a string — including the server, which produces the Mongolian text a 1C
 * refusal reaches a customer as. A typed catalogue object costs one exhaustive
 * `Record` and gives the same three things that actually matter here: a missing
 * translation is a **compile error**, the catalogue is one file a translator can
 * read, and the server and the page share it. Recorded as D10 in
 * `docs/decisions.md`.
 *
 * Mongolian is the default and English is the fallback, not the other way
 * round: this is a kiosk in Ulaanbaatar and the English is for the tourist.
 */
export type Locale = 'mn' | 'en'

export const LOCALES: readonly Locale[] = ['mn', 'en']
export const DEFAULT_LOCALE: Locale = 'mn'

/**
 * What each language calls itself.
 *
 * Endonyms, not translations: a customer looking for their own language scans
 * for the word they already recognise, and "Mongolian" is no help to somebody
 * who cannot read the screen it is written on. This is also why the switch is
 * never labelled — a globe and three endonyms need no caption in any language.
 */
export const LOCALE_NAMES: Record<Locale, string> = {
  mn: 'Монгол',
  en: 'English'
}

const mn = {
  'welcome.tap': 'Дэлгэцэнд хүрч захиална уу',
  'welcome.closed': 'Салбар түр хаалттай байна',
  'welcome.offline': 'Интернэт холболтгүй — хадгалсан цэс',

  'orderType.title': 'Үйлчлүүлэх хэлбэрээ сонгоно уу',
  'orderType.eatIn': 'Зааланд',
  'orderType.takeOut': 'Авч явах',
  'orderType.eatInHint': 'Зааланд тухлан суугаарай',
  'orderType.takeOutHint': 'Бэлтгүүлээд авч явах',
  'orderType.perLineHint': 'Бүтээгдэхүүн бүрээр сагсанд тусад нь өөрчлөх боломжтой',

  'menu.title': 'Цэс',
  'menu.items': 'бүтээгдэхүүн',
  'menu.startOver': 'Эхнээс нь эхлэх',
  'menu.empty': 'Энэ ангилалд одоогоор бүтээгдэхүүн алга',
  'menu.search': 'Хайх',
  'menu.searchNothing': 'Тохирох илэрц олдсонгүй',
  'menu.searchClear': 'Арилгах',
  'menu.searchResults': 'Хайлтын илэрц',
  'menu.searchOf': '/',
  'menu.searchBrowseAll': 'Бүх цэсийг үзэх',
  'menu.searchDropWord': 'Сүүлийн үгийг хасах',
  'menu.searchTryThese': 'Танд санал болгох бүтээгдэхүүн',
  'menu.categories': 'Ангилал',
  'menu.language': 'Хэл сонгох',
  'menu.keyboardClose': 'Товчлуур нуух',
  'menu.soldOut': 'Дууссан',
  'menu.eatInOnly': 'Зөвхөн зааланд',
  'menu.cart': 'Сагс',
  'menu.stale': 'Үнэ шинэчлэгдэж байж болзошгүй',

  'product.hot': 'Халуун',
  'product.ice': 'Мөстэй',
  'product.chooseTemperature': 'Халуун эсвэл мөстэйг сонгоно уу',
  'product.shots': 'Эспрессо шот',
  'product.change': 'Change',
  'product.sizeStandard': 'Стандарт',
  'product.extras': 'Нэмэлт сонголт',
  'product.add': 'Сагсанд нэмэх',
  'product.quantity': 'Тоо ширхэг',

  'cart.empty': 'Таны сагс хоосон байна',
  'cart.title': 'Таны захиалга',
  'cart.subtotal': 'Захиалгын дүн',
  'cart.vat': 'НӨАТ (10%)',
  'cart.summary': 'Төлбөрийн дэлгэрэнгүй',
  'cart.discount': 'Хөнгөлөлт',
  'cart.total': 'Нийт төлөх дүн',
  'cart.checkout': 'Төлбөр төлөх',
  'cart.addMore': 'Нэмж сонгох',
  'cart.remove': 'Хасах',
  'cart.removedUnavailable': 'Боломжгүй болсон бүтээгдэхүүнийг сагснаас хаслаа',

  'payment.title': 'Төлбөрийн хэлбэрээ сонгоно уу',
  'payment.cash': 'Бэлэн мөнгө',
  'payment.card': 'Банкны карт',
  'payment.qpay': 'QPay',
  'payment.due': 'Төлөх дүн',
  'payment.offline': 'Сүлжээгүй тул төлбөр хийх боломжгүй',
  'payment.offlineBody': 'Сүлжээ тасарсан тул энд төлбөр хийх боломжгүй. Кассанд хандана уу',
  'payment.notConfigured': 'Төхөөрөмж тохируулаагүй байна',
  'payment.cancel': 'Цуцлах',
  'payment.terminalWaiting': 'Картаа ПОС машинд уншуулна уу',
  'payment.terminalHint': 'Гүйлгээг шалгаж байна...',
  'payment.terminalApproved': 'Төлбөр амжилттай баталгаажлаа',
  'payment.terminalDeclined': 'Гүйлгээ амжилтгүй боллоо (төлбөр хасагдаагүй)',
  'payment.qpayScan': 'QR кодыг уншуулж төлнө үү',
  'payment.qpayWaiting': 'Төлбөр төлөгдөхийг хүлээж байна',
  'payment.qpayCheck': 'Төлбөр шалгах',
  'payment.qpayNotYet': 'Төлбөр хараахан баталгаажаагүй байна',
  'payment.company': 'Байгууллагын баримт (НӨАТ)',
  'payment.companyRegNo': 'Байгууллагын регистрийн дугаар',
  'payment.companyLookup': 'Шалгах',
  'payment.companyHint': 'Регистрийн дугаараар НӨАТ-ын баримт авах',
  'payment.coupon': 'Купон',
  'payment.couponCode': 'Купоны код',
  'payment.couponHint': 'Кодоо бичих эсвэл уншуулах',
  'payment.couponApply': 'Хэрэглэх',
  'payment.couponRowHint': 'Хөнгөлөлтийн кодоо оруулах',
  'payment.couponLocked': 'Захиалга үүссэн тул өөрчлөх боломжгүй',

  'success.title': 'Баярлалаа!',
  'success.number': 'Таны захиалгын дугаар',
  'success.wait': 'Дэлгэцээр дуудахыг хүлээнэ үү',
  'success.done': 'Дуусгах',
  'success.printing': 'Баримт хэвлэж байна...',
  'success.printFailed': 'Баримт хэвлэгдсэнгүй. Ажилтанд хандана уу',

  'boot.connecting': 'Холбогдож байна...',
  'boot.loadingMenu': 'Цэс ачаалж байна...',
  'boot.notEnrolled': 'Энэ киоск бүртгэгдээгүй байна',
  'boot.noCache': 'Интернэт холболтгүй бөгөөд хадгалсан цэс алга',
  'boot.retry': 'Дахин оролдох',

  'recovery.title': 'Дуусаагүй гүйлгээ',
  'recovery.body': 'Серверт бүртгэгдээгүй төлбөрийн гүйлгээ байна.',
  'recovery.retry': 'Дахин илгээх',
  'refusal.branch-closed': 'Салбар түр хаалттай байна',
  'refusal.price-mismatch': 'Үнэ шинэчлэгдсэн тул сагсаа дахин шалгана уу',
  'refusal.unknown-product': 'Энэ бүтээгдэхүүн одоогоор бэлэн биш байна',
  'refusal.out-of-stock': 'Бүтээгдэхүүний нөөц дууссан байна',
  'refusal.not-orderable': 'Энэ бүтээгдэхүүнийг одоогоор захиалах боломжгүй',
  'refusal.no-shift': 'Ээлж нээгдээгүй байна. Ажилтанд хандана уу',
  'refusal.already-exists': 'Энэ захиалга аль хэдийн бүртгэгдсэн байна',
  'refusal.qpay-not-configured': 'QPay төлбөрийн хэрэгсэл тохируулаагүй байна',
  'refusal.qpay-unavailable': 'QPay үйлчилгээ түр саатсан байна',
  'refusal.qpay-not-settled': 'QPay төлбөр бүрэн баталгаажаагүй байна. Ажилтанд хандана уу',
  'refusal.company-not-found': 'Байгууллагын регистрийн дугаар олдсонгүй',
  'refusal.ebarimt-unavailable': 'И-баримтын үйлчилгээ түр саатсан байна (таны дугаар зөв байна)',
  'refusal.bad-pin': 'ПИН код буруу байна',
  'refusal.shift-already-open': 'Ээлж аль хэдийн нээгдсэн байна',
  'refusal.unknown-staff': 'Ажилтны бүртгэл олдсонгүй',
  'refusal.shift-not-closable': 'Бэлэн мөнгөний үлдэгдлээ тоолж оруулна уу',
  'refusal.terminal-declined': 'Картын гүйлгээ амжилтгүй боллоо (төлбөр хасагдаагүй)',
  'refusal.terminal-unresolved': 'ПОС терминал хариу өгсөнгүй. Ажилтанд хандана уу',
  'refusal.offline': 'Интернэт холболт тасарсан байна',
  'refusal.server-unreachable': 'Сүлжээ тасарсан тул захиалга бүртгэх боломжгүй. Кассанд хандана уу',
  /**
   * 1C would not hold the stock for this basket, asked *before* the card is
   * charged. The kiosk shows 1C's own sentence beside this one when it sent
   * one — «нөөц хүрэлцэхгүй» names the actual problem where this cannot.
   */
  'refusal.onec-refused': 'Захиалгыг баталгаажуулах боломжгүй байна. Кассанд хандана уу',
  /**
   * The server answered and answered badly — a 500, a body that is not JSON, a
   * proxy's HTML. Distinct from `server-unreachable` on purpose: the network is
   * fine and telling a customer otherwise sends them away with the wrong story.
   */
  'refusal.server-error': 'Системд алдаа гарлаа. Кассанд хандана уу',
  'refusal.unknown': 'Алдаа гарлаа. Ажилтанд хандана уу',
  'menu.tooStale': 'Цэс шинэчлэгдээгүй тул захиалга авах боломжгүй байна. Ажилтанд хандана уу',
  'attendant.void': 'Цуцлах',
  'refusal.order-already-paid': 'Төлбөр төлөгдсөн захиалгыг цуцлах боломжгүй',
  'refusal.too-many-attempts': 'Хэт олон удаа буруу орууллаа. Түр хүлээнэ үү',
  'refusal.ebarimt-phone-not-wanted': 'Энэ захиалгад утасны дугаар шаардлагагүй',
  'refusal.ebarimt-phone-failed': 'Утасны дугаарыг илгээж чадсангүй. Дахин оролдоно уу',
  'shift.badPin': 'ПИН код буруу байна',
  'shift.lock': 'Түгжих',
  'recovery.unresolved': 'Баталгаажаагүй төлбөр',
  'recovery.unresolvedBody':
    'ПОС терминал хариу өгсөнгүй. Картаас төлбөр хасагдсан эсэхийг ажилтан терминалын тайлангаас шалгана уу.',
  'recovery.confirmTaken': 'Төлбөр хасагдсан',
  'recovery.writeOff': 'Хасагдаагүй',
  'recovery.resolvedBy': 'Шийдвэрлэсэн',
  'recovery.callAttendant': 'Ажилтанд хандана уу',

  'settings.title': 'Тохиргоо',
  'settings.device': 'Төхөөрөмж',
  'settings.printer': 'Хэвлэгч',
  'settings.terminal': 'ПОС терминал',
  'settings.scan': 'Хайх',
  'settings.testPrint': 'Туршилтын хэвлэлт',
  'settings.testTerminal': 'Холболт шалгах',
  'settings.noShell': 'Вэб горимд ажиллаж байна — төхөөрөмжийн тохиргоо байхгүй',
  'settings.problems': 'Анхаарах зүйлс',
  'settings.close': 'Хаах',

  'shift.title': 'Ээлжийн бүртгэл',
  'shift.start': 'Ээлж эхлүүлэх',
  'shift.end': 'Ээлж хаах',
  'shift.openingFloat': 'Кассын эхний үлдэгдэл',
  'shift.countedCash': 'Тоолсон бэлэн мөнгө',
  'shift.expected': 'Байх ёстой дүн',
  'shift.variance': 'Зөрүү',
  'shift.none': 'Идэвхтэй ээлж алга',
  'shift.pin': 'ПИН код',
  'shift.history': 'Өмнөх ээлжүүд',
  'shift.drift': 'Тооцоо зөрсөн',
  'shift.driftOk': 'Тооцоо тохирч байна',

  'attendant.title': 'Ажилтны цэс',
  'attendant.recent': 'Сүүлийн захиалгууд',
  'attendant.noOrders': 'Одоогоор захиалга хийгдээгүй байна',
  'attendant.fiscal': 'И-баримт',
  'attendant.fiscalQueued': 'Илгээхийг хүлээж буй',
  'attendant.fiscalFailed': 'Илгээж чадаагүй',

  'success.fiscalPending': 'И-баримт бүртгэгдэж байна...',
  'success.fiscalFiled': 'И-баримт амжилттай бүртгэгдлээ',
  'success.fiscalSkipped': 'Туршилтын худалдаа — И-баримт бүртгэгдэхгүй',
  /**
   * 1C took the sale while e-Barimt was down. The money is fine; the receipt
   * comes later, to the phone number the sheet below asks for.
   */
  'success.fiscalDeferred': 'Төлбөр амжилттай. И-баримт түр бүртгэгдсэнгүй',
  'success.phoneTitle': 'И-баримт авах утасны дугаар',
  'success.phoneHint': 'И-баримтын систем түр саатсан байна. Утасны дугаараа оруулбал И-баримтыг тань дараа илгээнэ.',
  'success.phoneSend': 'Илгээх',
  'success.phoneSkip': 'Алгасах',
  'success.phoneSent': 'И-баримтыг таны утсанд илгээнэ',
  'success.lottery': 'Сугалааны дугаар',
  'success.paid': 'Төлбөр төлөгдлөө',
  'success.takeToCounter': 'Энэ баримтыг кассанд өгнө үү',
  'success.unconfirmed': 'Сүлжээ тасарсан тул захиалга бүртгэгдээгүй. Баримтаа ажилтанд өгнө үү',

  'idle.title': 'Та захиалгаа үргэлжлүүлэх үү?',
  'idle.body': 'Хариу өгөхгүй бол таны захиалга цуцлагдана',
  'idle.seconds': 'секунд',
  'idle.continue': 'Үргэлжлүүлэх',
  'idle.finish': 'Эхнээс нь эхлэх',

  'payment.qpayExpiresIn': 'QR кодын хүчинтэй хугацаа',
  'payment.qpayExpired': 'QR кодын хугацаа дууслаа',
  'payment.qpayRenew': 'Шинэ QR код авах',
  'payment.terminalElapsed': 'ПОС терминалын хариуг хүлээж байна',

  'menu.lowStock': 'үлдсэн',

  'attendant.lines': 'Захиалгын дэлгэрэнгүй',
  'attendant.linesEmpty': 'Захиалгын мэдээлэл хоосон байна',
  'attendant.linesFailed': 'Захиалгын дэлгэрэнгүйг ачаалж чадсангүй',

  'common.back': 'Буцах',
  'common.cancel': 'Цуцлах',
  'common.confirm': 'Баталгаажуулах',
  'common.retry': 'Дахин оролдох',
  'common.loading': 'Ачаалж байна...',
  'common.error': 'Алдаа гарлаа',
  'common.ok': 'Ойлголоо',
  'pair.code': 'Бүртгэлийн код'
} as const

export type StringKey = keyof typeof mn

const en: Record<StringKey, string> = {
  'welcome.tap': 'Touch to order',
  'welcome.closed': 'This branch is closed',
  'welcome.offline': 'Offline — showing the saved menu',

  'orderType.title': 'Where are you having it?',
  'orderType.eatIn': 'Eat in',
  'orderType.takeOut': 'Take away',
  'orderType.eatInHint': 'Enjoy it in the café',
  'orderType.takeOutHint': 'Take it with you',
  'orderType.perLineHint': 'You can change this per item later',

  'menu.title': 'Menu',
  'menu.items': 'items',
  'menu.startOver': 'Start over',
  'menu.empty': 'Nothing in this category',
  'menu.search': 'Search',
  'menu.searchNothing': 'Nothing matches that',
  'menu.searchClear': 'Clear',
  'menu.searchResults': 'Search results',
  'menu.searchOf': 'of',
  'menu.searchBrowseAll': 'Browse the whole menu',
  'menu.searchDropWord': 'Drop the last word',
  'menu.searchTryThese': 'You might like these',
  'menu.categories': 'Categories',
  'menu.language': 'Language',
  'menu.keyboardClose': 'Close keyboard',
  'menu.soldOut': 'Sold out',
  'menu.eatInOnly': 'Eat in only',
  'menu.cart': 'Basket',
  'menu.stale': 'Prices may be out of date',

  'product.hot': 'Hot',
  'product.ice': 'Iced',
  'product.chooseTemperature': 'Choose hot or iced',
  'product.shots': 'Shots',
  'product.change': 'Change',
  'product.sizeStandard': 'Standard',
  'product.extras': 'Extras',
  'product.add': 'Add to basket',
  'product.quantity': 'Quantity',

  'cart.empty': 'Your basket is empty',
  'cart.title': 'Your order',
  'cart.subtotal': 'Subtotal',
  'cart.vat': 'VAT',
  'cart.summary': 'Order summary',
  'cart.discount': 'Discount',
  'cart.total': 'Total',
  'cart.checkout': 'Pay',
  'cart.addMore': 'Add more',
  'cart.remove': 'Remove',
  'cart.removedUnavailable': 'Items that are no longer available were removed',

  'payment.title': 'How would you like to pay?',
  'payment.cash': 'Cash',
  'payment.card': 'Card',
  'payment.qpay': 'QPay',
  'payment.due': 'To pay',
  'payment.offline': 'Unavailable offline',
  'payment.offlineBody': 'No connection, so payment cannot be taken here. Please order at the counter',
  'payment.notConfigured': 'Not set up',
  'payment.cancel': 'Cancel',
  'payment.terminalWaiting': 'Present your card',
  'payment.terminalHint': 'Verifying',
  'payment.terminalApproved': 'Approved',
  'payment.terminalDeclined': 'Declined',
  'payment.qpayScan': 'Scan the QR code',
  'payment.qpayWaiting': 'Waiting for payment',
  'payment.qpayCheck': 'Check payment',
  'payment.qpayNotYet': 'Payment has not arrived yet',
  'payment.company': 'Company receipt',
  'payment.companyRegNo': 'Registration number',
  'payment.companyLookup': 'Check',
  'payment.companyHint': 'A VAT receipt in your company’s name',
  'payment.coupon': 'Coupon',
  'payment.couponCode': 'Coupon code',
  'payment.couponHint': 'Type the code or scan it',
  'payment.couponApply': 'Apply',
  'payment.couponRowHint': 'Enter a discount code',
  'payment.couponLocked': 'The order has started and cannot change',

  'success.title': 'Thank you',
  'success.number': 'Your number',
  'success.wait': 'We will call it',
  'success.done': 'Done',
  'success.printing': 'Printing your receipt',
  'success.printFailed': 'The receipt did not print',

  'boot.connecting': 'Connecting',
  'boot.loadingMenu': 'Loading the menu',
  'boot.notEnrolled': 'This kiosk is not enrolled',
  'boot.noCache': 'Offline, and there is no saved menu',
  'boot.retry': 'Try again',

  'recovery.title': 'Unfinished payment',
  'recovery.body': 'This kiosk took a payment the server has not acknowledged.',
  'recovery.retry': 'Send again',
  'refusal.branch-closed': 'This shop is closed',
  'refusal.price-mismatch': 'Prices have changed \u2014 please check your basket',
  'refusal.unknown-product': 'That item is no longer available',
  'refusal.out-of-stock': 'That item has sold out',
  'refusal.not-orderable': 'That item cannot be ordered just now',
  'refusal.no-shift': 'No shift is open. Please call an attendant',
  'refusal.already-exists': 'That order has already been placed',
  'refusal.qpay-not-configured': 'QPay is not set up here',
  'refusal.qpay-unavailable': 'QPay is unavailable just now',
  'refusal.qpay-not-settled': 'QPay has not confirmed that payment in full. Please call an attendant',
  'refusal.company-not-found': 'No company has that registration number',
  'refusal.ebarimt-unavailable': 'e-Barimt is unavailable just now \u2014 the number is fine',
  'refusal.bad-pin': 'That PIN is not right',
  'refusal.shift-already-open': 'A shift is already open',
  'refusal.unknown-staff': 'Nobody by that name works here',
  'refusal.shift-not-closable': 'Count the cash before closing',
  'refusal.terminal-declined': 'The card was not charged',
  'refusal.terminal-unresolved': 'The terminal did not answer. Please call an attendant',
  'refusal.offline': 'No network',
  'refusal.server-unreachable': 'No connection, so the order cannot be registered. Please order at the counter',
  'refusal.onec-refused': 'This order could not be confirmed. Please order at the counter',
  'refusal.server-error': 'Something went wrong in the system. Please order at the counter',
  'refusal.unknown': 'Something went wrong. Please call an attendant',
  'menu.tooStale': 'This menu is too old to sell from. Please call an attendant',
  'attendant.void': 'Void',
  'refusal.order-already-paid': 'A paid order cannot be voided',
  'refusal.too-many-attempts': 'Too many attempts \u2014 please wait',
  'refusal.ebarimt-phone-not-wanted': 'This order does not need a phone number',
  'refusal.ebarimt-phone-failed': 'The number could not be sent. Please try again',
  'shift.badPin': 'That PIN is not right',
  'shift.lock': 'Lock',
  'recovery.unresolved': 'Payment not confirmed',
  'recovery.unresolvedBody':
    'The terminal did not answer. We cannot tell whether the card was charged \u2014 an attendant must check the terminal\u2019s own batch.',
  'recovery.confirmTaken': 'It was charged',
  'recovery.writeOff': 'It was not charged',
  'recovery.resolvedBy': 'Resolved by',
  'recovery.callAttendant': 'Please call an attendant',

  'settings.title': 'Settings',
  'settings.device': 'Device',
  'settings.printer': 'Printer',
  'settings.terminal': 'Card terminal',
  'settings.scan': 'Scan',
  'settings.testPrint': 'Test print',
  'settings.testTerminal': 'Check connection',
  'settings.noShell': 'Running in a browser — no device settings',
  'settings.problems': 'Problems',
  'settings.close': 'Close',

  'shift.title': 'Shift',
  'shift.start': 'Start shift',
  'shift.end': 'End shift',
  'shift.openingFloat': 'Opening float',
  'shift.countedCash': 'Counted cash',
  'shift.expected': 'Expected',
  'shift.variance': 'Variance',
  'shift.none': 'No shift is open',
  'shift.pin': 'PIN',
  'shift.history': 'Previous shifts',
  'shift.drift': 'The summary disagrees with its payments',
  'shift.driftOk': 'The summary agrees with its payments',

  'attendant.title': 'Attendant',
  'attendant.recent': 'Recent orders',
  'attendant.noOrders': 'Nothing sold yet',
  'attendant.fiscal': 'e-Barimt',
  'attendant.fiscalQueued': 'Queued',
  'attendant.fiscalFailed': 'Failed',

  'success.fiscalPending': 'Your e-Barimt is being filed',
  'success.fiscalFiled': 'e-Barimt filed',
  'success.fiscalSkipped': 'Test sale — no e-Barimt is filed',
  'success.fiscalDeferred': 'Paid. Your e-Barimt is delayed',
  'success.phoneTitle': 'Phone number for your e-Barimt',
  'success.phoneHint': 'e-Barimt is down just now. Enter your phone number and your e-Barimt will be sent to it later.',
  'success.phoneSend': 'Send',
  'success.phoneSkip': 'Skip',
  'success.phoneSent': 'Your e-Barimt will be sent to your phone',
  'success.lottery': 'Lottery',
  'success.paid': 'Paid',
  'success.takeToCounter': 'Take this receipt to the counter',
  'success.unconfirmed': 'No connection, so the order was not registered. Show this receipt to the staff',

  'idle.title': 'Are you still there?',
  'idle.body': 'Your order will be cleared if nobody answers',
  'idle.seconds': 'seconds',
  'idle.continue': 'Keep going',
  'idle.finish': 'Start over',

  'payment.qpayExpiresIn': 'This QR code expires in',
  'payment.qpayExpired': 'This QR code has expired',
  'payment.qpayRenew': 'Get a new code',
  'payment.terminalElapsed': 'Waiting for the terminal',

  'menu.lowStock': 'left',

  'attendant.lines': 'What was ordered',
  'attendant.linesEmpty': 'No lines',
  'attendant.linesFailed': 'Could not load what was ordered',

  'common.back': 'Back',
  'common.cancel': 'Cancel',
  'common.confirm': 'Confirm',
  'common.retry': 'Retry',
  'common.loading': 'Loading',
  'common.error': 'Something went wrong',
  'common.ok': 'OK',
  'pair.code': 'Enrolment code'
}

const catalogues: Record<Locale, Record<StringKey, string>> = { mn, en }

/**
 * Look a string up.
 *
 * Falls back to Mongolian rather than to the key: a key leaking onto a screen a
 * customer is reading is worse than the wrong language, and with the catalogues
 * exhaustively typed the fallback is unreachable except across a version skew.
 */
export function translate(locale: Locale, key: StringKey): string {
  return catalogues[locale][key] ?? catalogues[DEFAULT_LOCALE][key] ?? key
}

export function translator(locale: Locale): (key: StringKey) => string {
  const catalogue = catalogues[locale]
  return (key) => catalogue[key] ?? catalogues[DEFAULT_LOCALE][key] ?? key
}

export const catalogue = (locale: Locale): Record<StringKey, string> => catalogues[locale]
